import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatContainer, ConversationSidebar } from '../components/chat';
import { SourcePanel } from '../components/pdf';
import { useIntelligence } from '../contexts';
import api from '../../services/api';
import type { Conversation, ChatMessage, ProtocolContext, SourceCitation } from '../types';

export function Chat() {
  const {
    conversations,
    currentConversation,
    setCurrentConversation,
    loadConversations,
    protocols,
    setCurrentProtocol,
  } = useIntelligence();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [loadedMessages, setLoadedMessages] = useState<ChatMessage[]>([]);
  const [loadedProtocolContext, setLoadedProtocolContext] = useState<ProtocolContext | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  // Counter to force ChatContainer remount when clicking "New Chat"
  const [newChatKey, setNewChatKey] = useState(0);

  // Source panel state
  const [selectedSource, setSelectedSource] = useState<SourceCitation | null>(null);
  const [sourcePanelOpen, setSourcePanelOpen] = useState(false);

  // Detect mobile viewport
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Handle protocol_id query param (deep link from StudyOrganizer)
  useEffect(() => {
    const protocolIdParam = searchParams.get('protocol_id');
    if (protocolIdParam && protocols.length > 0) {
      const protocol = protocols.find(p => p.id === protocolIdParam);
      if (protocol) {
        setCurrentProtocol(protocol);
        // Clear the query param after setting the protocol
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, protocols, setCurrentProtocol, setSearchParams]);

  const handleSelectConversation = useCallback(async (conversation: Conversation) => {
    setCurrentConversation(conversation);
    setIsLoadingConversation(true);
    setMobileSidebarOpen(false); // Close mobile sidebar when selecting

    try {
      const response = await api.get(`/api/intelligence/conversations/${conversation.id}/messages`);
      const { messages, protocol_context } = response.data;

      // Convert messages to ChatMessage format
      const chatMessages: ChatMessage[] = messages.map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        protocolContext: msg.role === 'assistant' ? protocol_context : undefined,
      }));

      setLoadedMessages(chatMessages);
      setLoadedProtocolContext(protocol_context);

      // Set the protocol if conversation has one
      if (protocol_context?.protocol_id) {
        const protocol = protocols.find(p => p.id === protocol_context.protocol_id);
        if (protocol) {
          setCurrentProtocol(protocol);
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoadingConversation(false);
    }
  }, [setCurrentConversation, protocols, setCurrentProtocol]);

  const handleNewConversation = useCallback(() => {
    setCurrentConversation(null);
    setLoadedMessages([]);
    setLoadedProtocolContext(null);
    setCurrentProtocol(null);
    setMobileSidebarOpen(false); // Close mobile sidebar
    setNewChatKey(prev => prev + 1); // Force ChatContainer remount
  }, [setCurrentConversation, setCurrentProtocol]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await api.delete(`/api/intelligence/conversations/${id}`);
      await loadConversations();

      // If we deleted the current conversation, clear it
      if (currentConversation?.id === id) {
        handleNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  }, [loadConversations, currentConversation, handleNewConversation]);

  // Handle source click to open PDF viewer
  const handleSourceClick = useCallback((source: SourceCitation) => {
    setSelectedSource(source);
    setSourcePanelOpen(true);
  }, []);

  const handleCloseSourcePanel = useCallback(() => {
    setSourcePanelOpen(false);
    // Keep selectedSource for a moment to allow smooth close animation
    setTimeout(() => setSelectedSource(null), 300);
  }, []);

  return (
    <div className="flex h-full min-h-0">
      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Conversation sidebar - hidden on mobile unless open */}
      <div className={`
        md:relative md:block
        fixed top-12 bottom-0 left-0 z-40
        transform transition-transform duration-200 ease-in-out
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <ConversationSidebar
          conversations={conversations}
          currentConversation={currentConversation}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onMobileClose={() => setMobileSidebarOpen(false)}
          isMobile={isMobile}
        />
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex min-w-0 h-full">
        {/* Chat section */}
        <div className={`flex-1 flex flex-col min-w-0 h-full md:p-3 transition-all duration-300 ${sourcePanelOpen && !isMobile ? 'max-w-[calc(100%-500px)]' : ''}`}>
          <div className="h-full max-w-4xl mx-auto w-full">
            <ChatContainer
              key={currentConversation?.id || `new-${newChatKey}`}
              initialMessages={loadedMessages}
              initialProtocolContext={loadedProtocolContext}
              isLoadingConversation={isLoadingConversation}
              onSourceClick={handleSourceClick}
              onNewChat={handleNewConversation}
              onToggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Source panel - side panel on desktop, fullscreen overlay on mobile */}
        <SourcePanel
          source={selectedSource}
          isOpen={sourcePanelOpen}
          onClose={handleCloseSourcePanel}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
}
